import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  IconButton,
  Button,
  Grid,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from "@mui/material";
import { FavoriteBorder, Close, Favorite } from "@mui/icons-material";
import { removeCartItem } from "../../State/Cart/Action";
import {
  addItemToWishlist,
  isWishlistItemExist,
  removeWishlistItem,
} from "../../State/Wishlist/Action";
import { useConvertPrice } from "../../../Hooks/useConvertPrice";
import CloseIcon from "@mui/icons-material/Close";
import defaultImage from "../../../Common/productDefaultImage.png";

const CartItem = ({ item }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const jwt = localStorage.getItem("jwt");
  const role = localStorage.getItem("role");
  const [open, setOpen] = useState(false);
  const { convert, customerPriceWithCharge, payableAmount, selectedCurrency } =
    useConvertPrice();
  const [openCustomDialog, setOpenCustomDialog] = useState(false);
  const [selectedCustomSize, setSelectedCustomSize] = useState(null);
  const [isInWishlist, setIsInWishlist] = useState(false);
  const wishlistLoading = useSelector((state) => state.wishlist.loading);
  const [loadingProductId, setLoadingProductId] = useState(null);
  const { wishlist } = useSelector((store) => store);

  const handleOpenCustomDialog = () => {
    const savedSizes = JSON.parse(localStorage.getItem("customSizes")) || {};
    const sizeData = savedSizes[item?.product?.id] || null;
    setSelectedCustomSize(sizeData);
    setOpenCustomDialog(true);
  };

  const handleCloseCustomDialog = () => {
    setOpenCustomDialog(false);
    setSelectedCustomSize(null);
  };

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleConfirmRemove = () => {
    if (jwt) {
      dispatch(removeCartItem(item?.id));
    }
    setOpen(false);
  };

  const handleMoveToWishlist = async (productId) => {
    setLoadingProductId(productId);

    try {
      if (isInWishlist && jwt) {
        await dispatch(removeWishlistItem(productId));
        setIsInWishlist(false);
        setLoadingProductId(null);
      } else {
        await dispatch(addItemToWishlist(productId));
        setIsInWishlist(true);
        setLoadingProductId(null);
      }
    } finally {
      setLoadingProductId(null);
    }
  };

  useEffect(() => {
    if (jwt && role === "CUSTOMER" && item?.product?.id) {
      dispatch(isWishlistItemExist(item?.product?.id)).then((exists) => {
        if (typeof exists === "boolean") {
          setIsInWishlist(exists);
        }
      });
    }
  }, [
    dispatch,
    item?.product?.id,
    jwt,
    role,
    wishlist?.deletedWishlistItem,
    wishlist?.addWislistItem,
  ]);

  const productImage =
    item?.product?.images?.find(
      (img) =>
        img?.isPrimary &&
        img?.productColor?.color?.name === item?.variant?.productColor?.color?.name
    )?.fullUrl || defaultImage;

  const hasDiscount = item?.variant?.viewTotalDiscount > 0;
  const originalPrice = convert(
    customerPriceWithCharge(selectedCurrency, item?.variant?.viewCustomerAmount)
  );
  const salePrice = payableAmount(
    customerPriceWithCharge(selectedCurrency, item?.variant?.viewCustomerAmount),
    item?.variant?.viewTotalDiscount
  );
  const sizeLabel =
    item?.size?.toLowerCase() === "custom tailor"
      ? item?.size
      : item?.variant?.size?.name || item?.size;

  return (
    <Box className="px-4 py-5 sm:px-6">
      <Grid container spacing={2.5} alignItems="flex-start">
        <Grid
          item
          xs={4}
          sm={3}
          md={2.4}
          className="cursor-pointer"
          onClick={() => navigate(`/product/${item?.product?.id}`)}
        >
          <Box className="overflow-hidden rounded-xl bg-[#f6f2ed] aspect-[3/4]">
            <img
              src={productImage}
              alt={item.product?.title || "Product"}
              className="h-full w-full object-cover"
            />
          </Box>
        </Grid>

        <Grid item xs={8} sm={6} md={6.2}>
          <Box className="flex h-full flex-col">
            <Typography
              className="font-display italic text-[#333]"
              sx={{
                fontFamily: "'Playfair Display', Georgia, serif",
                fontStyle: "italic",
                fontSize: 16,
                fontWeight: 400,
                lineHeight: 1.35,
              }}
            >
              {item.product?.brand?.name}
            </Typography>

            <Typography
              sx={{
                fontFamily: "'Be Vietnam Pro', sans-serif",
                fontSize: 13,
                fontWeight: 300,
                color: "#707070",
                mt: 0.4,
                lineHeight: 1.45,
              }}
            >
              {item.product?.title}
            </Typography>

            <Box
              className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1"
              sx={{
                fontFamily: "'Be Vietnam Pro', sans-serif",
                fontSize: 13,
                color: "#333",
                fontWeight: 300,
              }}
            >
              {item?.variant?.productColor?.color?.name && (
                <span>
                  Color:{" "}
                  <span className="font-normal">
                    {item.variant.productColor.color.name}
                  </span>
                </span>
              )}
              {sizeLabel && (
                <button
                  type="button"
                  className={`bg-transparent border-0 p-0 text-[13px] ${
                    item?.size?.toLowerCase() === "custom tailor"
                      ? "cursor-pointer underline-offset-2 hover:underline"
                      : "cursor-default"
                  }`}
                  onClick={() => {
                    if (item?.size?.toLowerCase() === "custom tailor") {
                      handleOpenCustomDialog();
                    }
                  }}
                >
                  Size: <span className="font-normal">{sizeLabel}</span>
                </button>
              )}
            </Box>

            {item?.size?.toLowerCase() === "custom tailor" && (
              <Typography
                sx={{
                  fontFamily: "'Be Vietnam Pro', sans-serif",
                  fontSize: 12,
                  color: "#BB3D1F",
                  mt: 1,
                }}
              >
                Customization Charge: {convert(item?.product?.customizationPrice)}{" "}
                {selectedCurrency}
              </Typography>
            )}

            <Box
              className="mt-2.5 flex flex-wrap items-baseline gap-x-2"
              sx={{ fontFamily: "'Be Vietnam Pro', sans-serif" }}
            >
              <span className="text-[14px] font-semibold text-[#333]">
                {salePrice} {selectedCurrency}
              </span>
              {hasDiscount && (
                <>
                  <span className="text-[13px] text-[#9a9a9a] line-through">
                    {originalPrice} {selectedCurrency}
                  </span>
                  <span className="text-[12px] font-medium text-[#2f9e44]">
                    {item.variant.viewTotalDiscount}% OFF
                  </span>
                </>
              )}
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12} sm={3} md={3.4}>
          <Box className="flex items-center justify-end gap-4 sm:pt-0.5">
            <button
              type="button"
              onClick={handleClickOpen}
              className="inline-flex items-center gap-1 border-0 bg-transparent p-0 font-sans text-[13px] font-light text-[#707070] hover:text-[#333]"
            >
              <CloseIcon sx={{ fontSize: 16 }} />
              Remove
            </button>
            <button
              type="button"
              onClick={() => handleMoveToWishlist(item?.product?.id)}
              disabled={loadingProductId === item?.product?.id || wishlistLoading}
              className={`group/heart inline-flex items-center gap-1 border-0 bg-transparent p-0 font-sans text-[13px] font-light ${
                isInWishlist ? "text-red-500" : "text-[#707070] hover:text-red-500"
              }`}
            >
              {loadingProductId === item?.product?.id && wishlistLoading ? (
                <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-stone-400 border-t-transparent" />
              ) : isInWishlist ? (
                <Favorite sx={{ fontSize: 16 }} className="text-red-500" />
              ) : (
                <FavoriteBorder
                  sx={{ fontSize: 16 }}
                  className="text-[#707070] group-hover/heart:text-red-500"
                />
              )}
              Wishlist
            </button>
          </Box>
        </Grid>
      </Grid>

      <Dialog
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            borderRadius: "12px",
            minWidth: "350px",
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: "600", color: "#1f2937", fontSize: "1rem" }}>
          Confirm Removal
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: "#6b7280", fontSize: "0.875rem" }}>
            Are you sure you want to remove this item from your cart?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button
            onClick={handleClose}
            size="small"
            sx={{
              color: "#6b7280",
              fontWeight: "500",
              fontSize: "0.875rem",
              "&:hover": {
                backgroundColor: "#f3f4f6",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmRemove}
            variant="contained"
            size="small"
            sx={{
              backgroundColor: "#BB3D1F",
              color: "white",
              fontWeight: "600",
              borderRadius: "8px",
              px: 2,
              fontSize: "0.875rem",
              "&:hover": {
                backgroundColor: "#9e3319",
              },
            }}
            autoFocus
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={openCustomDialog}
        onClose={handleCloseCustomDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Custom Size Details
          <IconButton
            aria-label="close"
            onClick={handleCloseCustomDialog}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {selectedCustomSize ? (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Product Title"
                  value={item?.product?.title}
                  size="small"
                  fullWidth
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              {Object.entries(selectedCustomSize)
                .filter(
                  ([key, value]) =>
                    value !== null &&
                    value !== undefined &&
                    value !== "" &&
                    !["id", "productId"].includes(key)
                )
                .map(([key, value]) => {
                  const label = key
                    .replace(/([A-Z])/g, " $1")
                    .replace(/_/g, " ")
                    .replace(/^./, (str) => str.toUpperCase());

                  return (
                    <Grid item xs={12} sm={6} md={4} key={key}>
                      <TextField
                        label={label}
                        value={String(value)}
                        size="small"
                        fullWidth
                        InputProps={{ readOnly: true }}
                      />
                    </Grid>
                  );
                })}
            </Grid>
          ) : (
            <Typography color="text.secondary">
              No custom size details found.
            </Typography>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default CartItem;
